import { err, ok, safeTry } from "neverthrow";
import { match } from "ts-pattern";
import z from "zod";

import {
  buildInternalError,
  mustBeDefined,
  mustParseAny,
} from "@thalamos/common";

import { triageOutcomeSchema } from "../../types/formFieldSchemas.js";
import { FormSectionSchema } from "../../types/FormSection.js";
import { IncidentFormTypeSchema } from "../../types/incidentWorkItem.js";
import type { ActionInput } from "../ActionInput.js";

/**
 * Gets the incident type selection from the triage form.
 */
export function getIncidentTypeSelection({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">) {
  const result = safeTry(function* () {
    const triageForm = yield* ok(
      incidentWorkItem.forms.find(
        (form) => form.formType === IncidentFormTypeSchema.enum.triage,
      ),
    ).andThen(mustBeDefined());

    const triageMainSectionSchema = z
      .object({
        whatWillYouDo: z.object({
          value: triageOutcomeSchema,
          valueLabel: z.string(),
        }),
      })
      .loose();

    const triageMainSectionData = yield* ok(
      triageForm.sections.find(
        (section) => section.id === FormSectionSchema.enum.triageMain,
      )?.data,
    )
      .andThen(mustBeDefined())
      .andThen(mustParseAny(triageMainSectionSchema));

    return ok(triageMainSectionData.whatWillYouDo.value);
  });

  return result;
}

/**
 * Gets the incident type selection from the triage form.
 * This will only return values which are fully handled in the product
 * (currently 136 and MCA) and will error otherwise.
 */
export function getSupportedIncidentTypeSelection({
  incidentWorkItem,
}: Pick<ActionInput, "incidentWorkItem">) {
  const result = getIncidentTypeSelection({ incidentWorkItem }).andThen(
    (selection) =>
      match(selection)
        // Currently supported in the product
        .with("136", "mca", (value) => ok(value))
        // Currently unsupported in the product
        .with("voluntary", "other", (value) =>
          err(
            buildInternalError({
              message: `Unsupported triage outcome selected: ${value}`,
            }),
          ),
        )
        .exhaustive(),
  );

  return result;
}
